/**
 * Tonnage utilities for Traveller Ship Builder
 * Handles fractional ton weights based on canonical Traveller rules
 */

// Canonical fractional weights from Traveller rules
export const TONNAGE_STANDARDS = {
  // Weapons & Defences
  SMALL_WEAPON_MOUNT: 0.25,
  FIXED_MOUNT: 0,
  TURRET_MOUNT: 1,
  SMALL_BAY: 50,
  LARGE_BAY: 500,
  
  // Optional Systems
  REENTRY_CAPSULE: 0.5,
  EQUIPMENT_OVERHEAD: 0.05,
  
  // Power Systems (weight, not power output)
  POWER_PLANT_MIN: 1,
  SOLAR_PANEL_MIN: 1,
  
  // Solar panel power output (not weight)
  SOLAR_PANEL_POWER: {
    BASIC: 0.25,
    IMPROVED: 0.5,
    ENHANCED: 0.1,
    ADVANCED: 0.2
  }
} as const;

// System-specific validation rules
export const VALIDATION_RULES = {
  weapons: {
    smallWeapon: { min: 0.25, step: 0.25, max: 1, allowFractions: true },
    turret: { min: 1, step: 1, allowFractions: false },
    bay: { min: 50, step: 1, allowFractions: false },
    fixed: { min: 0, step: 0, allowFractions: false },
    smallWeaponMount: { min: 0.25, step: 0.25, max: 1, allowFractions: true },
    default: { min: 1, step: 1, allowFractions: false }
  },
  optionalSystems: {
    reentryCapsule: { min: 0.5, step: 0.5, allowFractions: true },
    equipmentOverhead: { min: 0.05, step: 0.05, allowFractions: true },
    default: { min: 1, step: 1, allowFractions: false }
  },
  powerSystems: {
    powerPlant: { min: 1, step: 1, allowFractions: false },
    solarPanel: { min: 1, step: 1, allowFractions: false },
    equipmentOverhead: { min: 0.05, step: 0.05, allowFractions: true },
    default: { min: 1, step: 1, allowFractions: false }
  },
  fuelSystems: {
    fuelTank: { min: 0.25, step: 0.25, allowFractions: true },
    fuelCapacity: { min: 0.25, step: 0.25, allowFractions: true },
    default: { min: 0.25, step: 0.25, allowFractions: true }
  }
} as const;

// Input field configurations
export const INPUT_CONFIGS = {
  WHOLE_TONS: { step: 1, min: 1 },
  SMALL_WEAPONS: { step: 0.25, min: 0.25 },
  REENTRY_CAPSULES: { step: 0.5, min: 0.5 },
  EQUIPMENT_OVERHEAD: { step: 0.05, min: 0.05 },
  FIXED_MOUNT: { step: 0, min: 0 }
} as const;

/**
 * Validates tonnage based on system type
 */
export const validateTonnage = (
  tons: number, 
  systemType: string,
  category: 'weapons' | 'optionalSystems' | 'powerSystems' | 'fuelSystems' = 'optionalSystems'
): { isValid: boolean; error?: string; correctedValue?: number } => {
  const categoryRules = VALIDATION_RULES[category];
  const rules = (categoryRules as any)[systemType] || categoryRules.default;
  
  // Check minimum
  if (tons < rules.min) {
    return {
      isValid: false,
      error: `Minimum ${rules.min} ton${rules.min !== 1 ? 's' : ''} required`,
      correctedValue: rules.min
    };
  }
  
  // Check maximum if specified
  if (rules.max && tons > rules.max) {
    return {
      isValid: false,
      error: `Maximum ${rules.max} ton${rules.max !== 1 ? 's' : ''} allowed`,
      correctedValue: rules.max
    };
  }
  
  // Check step increments
  if (rules.step > 0) {
    const remainder = tons % rules.step;
    if (remainder > 0.001) { // Allow for floating point precision
      const correctedValue = Math.round(tons / rules.step) * rules.step;
      return {
        isValid: false,
        error: `Must be in increments of ${rules.step} ton${rules.step !== 1 ? 's' : ''}`,
        correctedValue
      };
    }
  }
  
  return { isValid: true };
};

/**
 * Formats tonnage for display
 */
export const formatTonnage = (tons: number): string => {
  if (tons === 0) return "0 tons";
  if (tons === 1) return "1 ton";
  
  // Show decimal places for fractional tons
  if (tons < 1) {
    return `${tons} tons`;
  }
  
  // For whole numbers, show as integer
  if (Number.isInteger(tons)) {
    return `${tons} tons`;
  }
  
  // For mixed numbers, show decimal
  return `${tons} tons`;
};

/**
 * Rounds tonnage to nearest valid increment
 */
export const roundTonnage = (
  tons: number, 
  step: number = 1
): number => {
  if (step === 0) return tons;
  return Math.round(tons / step) * step;
};

/**
 * Gets input configuration for a specific system type
 */
export const getInputConfig = (
  systemType: string,
  category: 'weapons' | 'optionalSystems' | 'powerSystems' | 'fuelSystems' = 'optionalSystems'
) => {
  const categoryRules = VALIDATION_RULES[category];
  const rules = (categoryRules as any)[systemType] || categoryRules.default;
  
  return {
    step: rules.step,
    min: rules.min,
    max: rules.max,
    allowFractions: rules.allowFractions
  };
};

/**
 * Validates if a system type allows fractional tons
 */
export const allowsFractionalTons = (
  systemType: string,
  category: 'weapons' | 'optionalSystems' | 'powerSystems' | 'fuelSystems' = 'optionalSystems'
): boolean => {
  const categoryRules = VALIDATION_RULES[category];
  const rules = (categoryRules as any)[systemType] || categoryRules.default;
  return rules.allowFractions;
};

/**
 * Gets the appropriate step value for a system type
 */
export const getStepValue = (
  systemType: string,
  category: 'weapons' | 'optionalSystems' | 'powerSystems' | 'fuelSystems' = 'optionalSystems'
): number => {
  const categoryRules = VALIDATION_RULES[category];
  const rules = (categoryRules as any)[systemType] || categoryRules.default;
  return rules.step;
};

/**
 * Gets the minimum value for a system type
 */
export const getMinValue = (
  systemType: string,
  category: 'weapons' | 'optionalSystems' | 'powerSystems' | 'fuelSystems' = 'optionalSystems'
): number => {
  const categoryRules = VALIDATION_RULES[category];
  const rules = (categoryRules as any)[systemType] || categoryRules.default;
  return rules.min;
};
